// Cookie consent (UK GDPR / PECR). Non-essential scripts (Mouseflow analytics,
// Meta Pixel) only load once the visitor has opted in to that category.

import { getAttribution } from './leadAttribution'

export interface CookieConsent {
    analytics: boolean
    marketing: boolean
    updatedAt: string
}

const STORAGE_KEY = 'cookie_consent'
// Bump when categories or vendors change so everyone is asked again.
const CONSENT_VERSION = 1

const META_PIXEL_ID = '1851516962952910'
const MOUSEFLOW_SRC = '//cdn.mouseflow.com/projects/cda23aaa-29ba-4aba-bf07-4d3a4733d6b6.js'

const OPEN_SETTINGS_EVENT = 'cookie-consent:open'

export const getConsent = (): CookieConsent | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        return parsed.version === CONSENT_VERSION ? parsed : null
    } catch {
        return null
    }
}

export const hasMarketingConsent = () => getConsent()?.marketing === true

// The Meta Pixel belongs to the Sentinel Forces campaign, so it only runs for
// visitors tagged as coming from them (the /forces link, utm_source or referrer).
export const isSentinelForcesVisitor = () => getAttribution().leadSource === 'sentinelforces'

const loadMouseflow = () => {
    if (document.querySelector(`script[src="${MOUSEFLOW_SRC}"]`)) return
    window._mfq = window._mfq || []
    const mf = document.createElement('script')
    mf.type = 'text/javascript'
    mf.defer = true
    mf.src = MOUSEFLOW_SRC
    document.getElementsByTagName('head')[0].appendChild(mf)
}

const loadMetaPixel = () => {
    if (window.fbq) return
    if (!isSentinelForcesVisitor()) return
    /* eslint-disable */
    // prettier-ignore
    ;(function(f: any,b: any,e: any,v: any,n?: any,t?: any,s?: any){
      if(f.fbq)return;
      n=f.fbq=function(){
        n.callMethod ? n.callMethod.apply(n,arguments) : n.queue.push(arguments);
      };
      if(!f._fbq)f._fbq=n;
      n.push=n;n.loaded=true;n.version='2.0';n.queue=[];
      t=b.createElement(e);t.async=true;t.src=v;
      s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s);
    })(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */

    window.fbq!('init', META_PIXEL_ID)
    window.fbq!('trackSingle', META_PIXEL_ID, 'PageView')
}

// Loads whichever scripts the stored consent allows. Safe to call repeatedly.
// Called after the visit's attribution is captured, so the Forces check is accurate.
export const applyConsent = () => {
    const consent = getConsent()
    if (consent?.analytics) loadMouseflow()
    if (consent?.marketing) loadMetaPixel()
}

const clearCookies = (names: RegExp) => {
    const host = window.location.hostname
    const domains = ['', host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`]
    document.cookie.split(';').forEach((c) => {
        const name = c.split('=')[0].trim()
        if (!names.test(name)) return
        domains.forEach((d) => {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ''}`
        })
    })
}

export const saveConsent = (choice: { analytics: boolean; marketing: boolean }) => {
    const previous = getConsent()
    const consent = { ...choice, updatedAt: new Date().toISOString() }
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, version: CONSENT_VERSION }))
    } catch {
        // Storage blocked: consent applies to this page load only.
    }

    const withdrawn = (previous?.analytics && !choice.analytics) || (previous?.marketing && !choice.marketing)
    if (withdrawn) {
        // Loaded scripts can't be unloaded, so remove their cookies and reload clean.
        if (!choice.analytics) clearCookies(/^mf_/)
        if (!choice.marketing) clearCookies(/^_fb[pc]$/)
        window.location.reload()
        return
    }

    if (choice.analytics) loadMouseflow()
    if (choice.marketing) loadMetaPixel()
}

export const openCookieSettings = () => window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT))

export const onOpenCookieSettings = (handler: () => void) => {
    window.addEventListener(OPEN_SETTINGS_EVENT, handler)
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, handler)
}

export { META_PIXEL_ID }
